import { Box } from "@mui/material";
import { motion } from "framer-motion";
import { PlayingCardFace } from "./PlayingCardFace";

type Props = {
  widthPx?: number;
  /** `false` = one riffle cycle. `true` = loop forever. */
  loop?: boolean;
  /** `table` = centered on green felt, larger motion. `hand` = legacy strip (unused in main flow). */
  placement?: "hand" | "table";
};

/** Riffle-shuffle motion using card backs. */
export function HandShuffleOverlay({ widthPx, loop = false, placement = "hand" }: Props) {
  const isTable = placement === "table";
  const w = widthPx ?? (isTable ? 76 : 44);
  const left = [0, 1, 2];
  const right = [0, 1, 2];

  const spread = isTable ? 96 : 72;
  const yAmp = isTable ? 12 : 8;
  const rot = isTable ? 9 : 7;

  const transitionBase = {
    duration: 2.1,
    ease: "easeInOut" as const,
    repeat: loop ? Infinity : 0,
  };

  const inner = (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        height: "100%",
        maxWidth: isTable ? 520 : 420,
        mx: "auto",
      }}
    >
      {left.map((i) => (
        <motion.div
          key={`sl-${i}`}
          style={{
            position: "absolute",
            left: isTable ? "18%" : 24,
            top: isTable ? 16 + i * 3 : 12 + i * 2,
          }}
          animate={{
            x: [0, spread + i * 6, 0],
            y: [0, -yAmp + i * 2, 0],
            rotate: [0, rot, 0],
          }}
          transition={{
            ...transitionBase,
            delay: i * 0.07,
          }}
        >
          <PlayingCardFace rankCode="A" suitCode="S" back widthPx={w} />
        </motion.div>
      ))}
      {right.map((i) => (
        <motion.div
          key={`sr-${i}`}
          style={{
            position: "absolute",
            right: isTable ? "18%" : 24,
            top: isTable ? 16 + i * 3 : 12 + i * 2,
          }}
          animate={{
            x: [0, -spread - i * 6, 0],
            y: [0, -yAmp + i * 2, 0],
            rotate: [0, -rot, 0],
          }}
          transition={{
            ...transitionBase,
            delay: 0.18 + i * 0.07,
          }}
        >
          <PlayingCardFace rankCode="A" suitCode="S" back widthPx={w} />
        </motion.div>
      ))}
    </Box>
  );

  if (isTable) {
    return (
      <Box
        className="table-shuffle-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 15,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "92%",
            height: { xs: 150, sm: 200 },
            maxWidth: 480,
          }}
        >
          {inner}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        bottom: 4,
        height: 120,
        pointerEvents: "none",
        zIndex: 0,
        opacity: 0.9,
      }}
    >
      {inner}
    </Box>
  );
}
